import {Typography, css} from '@mui/material';
import {ConfigObjectType, DlMuiDynamicIcon} from '@wallaby-ui/core';
import {
  Formatters,
  LightAccordionButtonHeader,
  MerieSchema,
  useFormatters,
  getRiskFactorMedicationLabel,
  GetDRPMedicationDataProps,
} from '@wallaby-ui/document';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Modal} from 'react-bootstrap';

import {DRPMedicationData, MedicationType} from './HospitalizationPhRecommendationPanelV2';

type ChangeLogEntry = MerieSchema.components['schemas']['PharmaRecommendationChangelogEntry'];
type ChangeLog = ChangeLogEntry[];
type RecommendationRemovedFields = MerieSchema.components['schemas']['PharmaRecommendationEntityChangelogRemovedSimpleFields'];
type RecommendationChangedFields = MerieSchema.components['schemas']['PharmaRecommendationEntityChangelogSimpleFields'];
type RecommendationAddedArrayEntries = MerieSchema.components['schemas']['PharmaRecommendationEntityChangelogArrayFields'];
type RecommendationRemovedArrayEntries = MerieSchema.components['schemas']['PharmaRecommendationEntityChangelogArrayFields'];
type DrpRemovedFields = MerieSchema.components['schemas']['DrugRelatedProblemEntityChangelogRemovedSimpleFields'];
type DrpChangedFields = MerieSchema.components['schemas']['DrugRelatedProblemEntityChangelogSimpleFields'];
type DrpAddedArrayEntries = MerieSchema.components['schemas']['DrugRelatedProblemEntityChangelogArrayFields'];
type DrpRemovedArrayEntries = MerieSchema.components['schemas']['DrugRelatedProblemEntityChangelogArrayFields'];
type EncounterRiskFactor = MerieSchema.components['schemas']['RiskFactorDto'];

export type ParsedChangeRow = {
  key: string;
  value: any;
};

export type ParsedChange = {
  _id: string;
  user: string;
  timestamp: string;
  addRows: ParsedChangeRow[];
  editRows: ParsedChangeRow[];
  deleteRows: ParsedChangeRow[];
};

export type Organization = {
  id: number;
  label: string;
};

type ChangeLogProps = {
  config: ConfigObjectType;
  changelog?: ChangeLog;
  medications: MedicationType[];
  onClose: () => void;
  getDRPMedicationData: (props: GetDRPMedicationDataProps) => DRPMedicationData;
  encounterRiskFactors: EncounterRiskFactor[];
  organizations: Organization[];
};

const richTextFieldNames = [
  'summary',
  'conclusion',
  'plannedInterventionsUserComment',
  'interventionNotAcceptedReasonUserComment',
  'notes',
];

const isOmmitted = (
  key: string,
  fields: RecommendationRemovedFields | DrpRemovedFields | RecommendationChangedFields | DrpChangedFields
) => {
  return richTextFieldNames.includes(key) && fields[`${key}Formatted`] != null;
};

const getPrFormatters = (
  formatters: Formatters,
  riskFactorIdToEntryMap: Record<number, EncounterRiskFactor>,
  organizationIdToEntryMap: Record<number, Organization>
) => {
  const {t, tEnum, formatDate, formatDateTime} = formatters;
  return {
    riskiness: {label: t('hospitalization.detail.phRec.riskiness'), formatter: (v) => tEnum('Riskiness', v)},
    lastCheckedOn: {label: t('hospitalization.detail.phRec.lastCheckedDate'), formatter: (v) => formatDateTime(v)},
    checked: {label: t('hospitalization.detail.phRec.checked'), formatter: (v) => (v ? t('global.yes') : t('global.no'))},
    organizationDepartmentId: {
      label: t('hospitalization.detail.phRec.organizationDepartmentId'),
      formatter: (v) => organizationIdToEntryMap[v]?.label || v,
    },
    lastPublishedOn: {label: t('hospitalization.detail.phRec.lastPublishDate'), formatter: (v) => formatDateTime(v)},
    summary: {label: t('hospitalization.detail.phRec.summary')},
    conclusion: {label: t('hospitalization.detail.phRec.conclusion')},
    summaryFormatted: {label: t('hospitalization.detail.phRec.summary')},
    conclusionFormatted: {label: t('hospitalization.detail.phRec.conclusion')},
    conclusionIsWithoutRecommendation: {
      label: t('hospitalization.detail.phRec.conclusionIsWithoutRecommendation'),
      formatter: (v) => (v ? t('global.yes') : t('global.no')),
    },
    followUpDate: {label: t('patient.list.grid.pharmaRecommendationFollowUpDate'), formatter: (v) => formatDate(v)},
    procedures: {label: t('hospitalization.detail.phRec.procedures'), formatter: (v) => tEnum('Procedure', v)},
    riskFactors: {
      label: t('hospitalization.detail.phRec.riskFactors'),
      formatter: (v) => {
        if (v.riskFactorType === 'OTHER') {
          return `${tEnum('RiskFactorTypeEnum', 'OTHER')} - ${v.riskFactorUserGivenName}`;
        }
        const riskFactor = riskFactorIdToEntryMap[v.riskFactorId];
        const medicationLabel = riskFactor ? getRiskFactorMedicationLabel(riskFactor) : '';
        return `${tEnum('RiskFactorTypeEnum', v.riskFactorType)}${medicationLabel ? ` - ${medicationLabel}` : ''}`;
      },
    },
  };
};
export type PrFormattersType = ReturnType<typeof getPrFormatters>;

const getDrpFormatters = (formatters: Formatters, newDrpLabelFormatter: (drp: GetDRPMedicationDataProps) => string) => {
  const {t, tEnum, formatDate} = formatters;
  return {
    newOrUpdatedDrp: {
      label: t('hospitalization.detail.phRec.changelog.drp.new'),
      formatter: (v) => newDrpLabelFormatter(v),
    },
    medicationName: {label: t('hospitalization.detail.phRec.drp.medicationName')},
    medicationUserInput: {label: t('hospitalization.detail.phRec.drp.medicationName')},
    medicationAtc: {label: t('hospitalization.detail.phRec.drp.medicationAtcId')},
    type: {label: t('hospitalization.detail.phRec.drp.type'), formatter: (v) => tEnum('DrpType', v)},
    manifestation: {label: t('hospitalization.detail.phRec.drp.manifestation'), formatter: (v) => tEnum('DrpManifestation', v)},
    causes: {label: t('hospitalization.detail.phRec.drp.causes'), formatter: (v) => tEnum('DrpCause', v)},
    plannedInterventions: {
      label: t('hospitalization.detail.phRec.drp.plannedInterventions'),
      formatter: (v) => tEnum('DrpPlannedIntervention', v),
    },
    plannedInterventionsUserComment: {
      label: t('hospitalization.detail.phRec.drp.plannedInterventionsUserComment'),
    },
    plannedInterventionsUserCommentFormatted: {
      label: t('hospitalization.detail.phRec.drp.plannedInterventionsUserComment'),
    },
    interventionAcceptance: {
      label: t('hospitalization.detail.phRec.drp.interventionAcceptance'),
      formatter: (v) => tEnum('DrpInterventionAcceptance', v),
    },
    interventionNotAcceptedReason: {
      label: t('hospitalization.detail.phRec.drp.interventionNotAcceptedReason'),
      formatter: (v) => tEnum('DrpInterventionNotAcceptedReason', v),
    },
    interventionNotAcceptedReasonUserComment: {
      label: t('hospitalization.detail.phRec.drp.interventionNotAcceptedReasonUserComment'),
    },
    interventionNotAcceptedReasonUserCommentFormatted: {
      label: t('hospitalization.detail.phRec.drp.interventionNotAcceptedReasonUserComment'),
    },
    status: {label: t('hospitalization.detail.phRec.drp.status'), formatter: (v) => tEnum('DrpStatus', v)},
    statusAssessedOn: {label: t('hospitalization.detail.phRec.drp.statusAssessedOn'), formatter: (v) => formatDate(v)},
    notes: {label: t('hospitalization.detail.phRec.drp.notes')},
    notesFormatted: {label: t('hospitalization.detail.phRec.drp.notes')},
  };
};
export type DrpFormattersType = ReturnType<typeof getDrpFormatters>;

export const parseChanges = (
  changelog: ChangeLog,
  formatters: Formatters,
  newDrpLabelFormatter: (drp: GetDRPMedicationDataProps) => string,
  riskFactorIdToEntryMap: Record<number, EncounterRiskFactor>,
  organizationIdToEntryMap: Record<number, Organization>
): ParsedChange[] => {
  if (!changelog || changelog.length === 0) return [];

  return changelog.flatMap((entry: ChangeLogEntry, index: number) => {
    const addRows: ParsedChangeRow[] = [];
    const editRows: ParsedChangeRow[] = [];
    const deleteRows: ParsedChangeRow[] = [];

    if (entry.pharmaRecommendation) {
      const {removedFields, changedFields, addedArrayEntries, removedArrayEntries} = entry.pharmaRecommendation;
      const prFormatters = getPrFormatters(formatters, riskFactorIdToEntryMap, organizationIdToEntryMap);
      parseChangeLogEntryChanges(
        '',
        removedFields,
        changedFields,
        addedArrayEntries,
        removedArrayEntries,
        prFormatters,
        addRows,
        editRows,
        deleteRows
      );
    }
    if (entry.drugRelatedProblems) {
      parseDrpChanges(entry, formatters, newDrpLabelFormatter, addRows, editRows, deleteRows);
    }

    if (addRows.length === 0 && editRows.length === 0 && deleteRows.length === 0) {
      return [];
    }

    return [
      {
        _id: `change-${index}`,
        user: entry.user,
        timestamp: entry.timestamp,
        addRows,
        editRows,
        deleteRows,
      },
    ];
  });
};

const HIDDEN_CHANGELOG_FIELDS = [
  'medicationId',
  'medicationIsActive',
  'medicationAtcId',
  'medicationAtcIsActive',
  'medicationAtcCode',
  'medicationAtcName',
];

const normalizeDrpChangedFields = (fields: Record<string, any> | null | undefined): Record<string, any> | null | undefined => {
  if (!fields) return fields;
  const result = {...fields};
  const code = result.medicationAtcCode;
  const name = result.medicationAtcName;
  if (code != null || name != null) {
    result.medicationAtc = [code, name].filter(Boolean).join(' ');
  }
  return result;
};

const normalizeDrpRemovedFields = (fields: Record<string, any> | null | undefined): Record<string, any> | null | undefined => {
  if (!fields) return fields;
  const result = {...fields};
  if ('medicationAtcCode' in result || 'medicationAtcName' in result) {
    result.medicationAtc = null;
  }
  return result;
};

const parseChangeLogEntryChanges = (
  prefix: string,
  removedFields: RecommendationRemovedFields | DrpRemovedFields,
  changedFields: RecommendationChangedFields | DrpChangedFields,
  addedArrayEntries: RecommendationAddedArrayEntries | DrpAddedArrayEntries,
  removedArrayEntries: RecommendationRemovedArrayEntries | DrpRemovedArrayEntries,
  formatters: PrFormattersType | DrpFormattersType,
  addRows: ParsedChangeRow[],
  editRows: ParsedChangeRow[],
  deleteRows: ParsedChangeRow[]
) => {
  const pre = prefix ? `${prefix} - ` : '';
  if (changedFields) {
    Object.keys(changedFields).forEach((key) => {
      if (!HIDDEN_CHANGELOG_FIELDS.includes(key)) {
        const value = changedFields[key];
        if (value !== null && value !== undefined && !isOmmitted(key, changedFields)) {
          const formatter = formatters[key];
          editRows.push({key: `${pre}${formatter?.label || key}`, value: formatter?.formatter ? formatter.formatter(value) : value});
        }
      }
    });
  }
  if (removedFields) {
    Object.keys(removedFields).forEach((key) => {
      if (!HIDDEN_CHANGELOG_FIELDS.includes(key)) {
        if (!isOmmitted(key, removedFields)) {
          const formatter = formatters[key];
          editRows.push({key: `${pre}${formatter?.label || key}`, value: ''});
        }
      }
    });
  }
  if (addedArrayEntries) {
    Object.keys(addedArrayEntries).forEach((key) => {
      if (key === 'drugRelatedProblems') return;
      const value = addedArrayEntries[key];
      processArrayEntries(pre, value, key, addRows, formatters[key]);
    });
  }
  if (removedArrayEntries) {
    Object.keys(removedArrayEntries).forEach((key) => {
      if (key === 'drugRelatedProblems') return;
      const value = removedArrayEntries[key];
      processArrayEntries(pre, value, key, deleteRows, formatters[key]);
    });
  }
};

const processArrayEntries = (prefix: string, entries: any[], key: string, rowsArray: ParsedChangeRow[], formatter) => {
  if (entries?.length > 0) {
    const formattedValues: any[] = [];
    entries.forEach((value: any) => {
      formattedValues.push(formatter?.formatter?.(value) || value);
    });
    rowsArray.push({key: `${prefix}${formatter?.label || key}`, value: formattedValues.sort().join(', ')});
  }
};

const parseDrpChanges = (
  entry: ChangeLogEntry,
  formatters: Formatters,
  newDrpLabelFormatter: (drp: GetDRPMedicationDataProps) => string,
  addRows: ParsedChangeRow[],
  editRows: ParsedChangeRow[],
  deleteRows: ParsedChangeRow[]
) => {
  const drpFormatters = getDrpFormatters(formatters, newDrpLabelFormatter);
  entry.drugRelatedProblems.forEach((drpChange: any) => {
    const {drp, removedFields, changedFields, addedArrayEntries, removedArrayEntries} = drpChange;
    const isAdded = entry.pharmaRecommendation?.addedArrayEntries?.drugRelatedProblems?.some((added) => added.id === drp.id) || false;

    if (isAdded) {
      addRows.push({
        key: drpFormatters.newOrUpdatedDrp.label,
        value: drpFormatters.newOrUpdatedDrp.formatter(drp),
      });
    }
    const prefix = drpFormatters.newOrUpdatedDrp.formatter(drp);
    const edit = isAdded ? addRows : editRows;
    parseChangeLogEntryChanges(
      prefix,
      normalizeDrpRemovedFields(removedFields),
      normalizeDrpChangedFields(changedFields),
      addedArrayEntries,
      removedArrayEntries,
      drpFormatters,
      addRows,
      edit,
      deleteRows
    );
  });
  const removedDrps = entry.pharmaRecommendation?.removedArrayEntries?.drugRelatedProblems;
  if (removedDrps && removedDrps.length > 0) {
    removedDrps.forEach((entry) => {
      deleteRows.push({
        key: drpFormatters.newOrUpdatedDrp.formatter(entry),
        value: '',
      });
    });
  }
};

function createIdMap<T extends {id: number | string}>(items: T[]): Record<T['id'], T> {
  const acc: Record<T['id'], T> = {} as Record<T['id'], T>;
  items.forEach((entry) => {
    acc[entry.id] = entry;
  });
  return acc;
}

export const HospitalizationPhRecommendationChangeLogV2: React.FC<ChangeLogProps> = ({
  changelog: changelogOriginal,
  onClose,
  config,
  medications,
  getDRPMedicationData,
  encounterRiskFactors,
  organizations,
}) => {
  const changelog = useMemo(() => (changelogOriginal ? [...changelogOriginal].reverse() : []), [changelogOriginal]);
  const formatters = useFormatters();
  const {t, formatDateTime} = formatters;
  const styles = getStyles();
  const [openIds, setOpenIds] = useState<string[]>([]);
  const [hasAutoOpenedFirst, setHasAutoOpenedFirst] = useState(false);

  const riskFactorIdToEntryMap = useMemo(() => createIdMap(encounterRiskFactors), [encounterRiskFactors]);
  const organizationIdToEntryMap = useMemo(() => createIdMap(organizations), [organizations]);
  const newDrpLabelFormatter = useCallback(
    (drp: GetDRPMedicationDataProps) => getDRPMedicationData(drp).fullMedicationLabel,
    [getDRPMedicationData]
  );
  const parsedChangeLog = useMemo(
    () => parseChanges(changelog, formatters, newDrpLabelFormatter, riskFactorIdToEntryMap, organizationIdToEntryMap),
    [changelog, formatters, newDrpLabelFormatter, riskFactorIdToEntryMap, organizationIdToEntryMap]
  );
  useEffect(() => {
    if (!hasAutoOpenedFirst && parsedChangeLog.length > 0) {
      const firstItemId = parsedChangeLog[0]._id;
      setOpenIds((prevOpenIds) => {
        if (!prevOpenIds.includes(firstItemId)) {
          return [...prevOpenIds, firstItemId];
        }
        return prevOpenIds;
      });
      setHasAutoOpenedFirst(true);
    }
  }, [parsedChangeLog, hasAutoOpenedFirst]);
  const toggleItem = (id: string) => {
    setOpenIds((prevOpenIds) => {
      if (prevOpenIds.includes(id)) {
        return prevOpenIds.filter((openId) => openId !== id);
      }
      return [...prevOpenIds, id];
    });
  };

  const renderRowGroup = (label: string, dataName: string, rows: ParsedChangeRow[]) => (
    <div css={styles.rowGroup} data-name={dataName}>
      <div css={styles.rowGroupLabel}>{label}</div>
      {rows.map((row) => (
        <div key={`${label}-${row.key}-${row.value}`} css={styles.row}>
          <div css={styles.key} data-name={'changelog-row-key'}>
            {row.key}
          </div>
          <div css={styles.value} data-name={'changelog-row-value'}>
            {row.value}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Modal className='dl-big-modal' show onHide={onClose} style={{borderRadius: 16}}>
      <div css={styles.modalContent}>
        <Modal.Header closeButton>
          <Modal.Title>
            <div css={styles.header}>
              <Typography variant='labelBold' style={{fontSize: 18}}>
                {t('hospitalization.detail.phRec.changelog.dialog.title')}
              </Typography>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body css={styles.modalBody}>
          {parsedChangeLog.map((change) => (
            <div key={change._id} css={styles.changelogItem(openIds.includes(change._id))} data-name={`changelog-item`}>
              <LightAccordionButtonHeader
                isOpen={openIds.includes(change._id)}
                setIsOpen={() => toggleItem(change._id)}
                css={styles.changelogItemHeader}
                data-name='change-header'
              >
                <div css={styles.changelogItemHeaderLeft}>
                  <DlMuiDynamicIcon
                    iconName='haidiUserIcon'
                    useFixedViewBox={12}
                    inputCss={styles.changelogItemHeaderIconButton}
                    svgIconInputCss={styles.changelogItemHeaderIcon}
                    iconTitle='hospitalization.detail.phRec.changelogUser'
                  />
                  <Typography css={styles.changelogItemHeaderText} variant='label' data-name='changelog-name'>
                    {change.user}
                  </Typography>
                  <div css={styles.changelogItemHeaderSpacer} />
                  <DlMuiDynamicIcon
                    iconName='haidiTimeIcon'
                    useFixedViewBox={12}
                    inputCss={styles.changelogItemHeaderIconButton}
                    svgIconInputCss={styles.changelogItemHeaderIcon}
                    iconTitle='hospitalization.detail.phRec.changelogTime'
                  />
                  <Typography css={styles.changelogItemHeaderTextShrink} variant='label' data-name='changelog-date'>
                    {formatDateTime(change.timestamp)}
                  </Typography>
                </div>
              </LightAccordionButtonHeader>

              {openIds.includes(change._id) && (
                <>
                  {change.addRows.length > 0 &&
                    renderRowGroup(t('hospitalization.detail.phRec.changelog.add'), 'added-rows', change.addRows)}
                  {change.editRows.length > 0 &&
                    renderRowGroup(t('hospitalization.detail.phRec.changelog.edit'), 'edited-rows', change.editRows)}
                  {change.deleteRows.length > 0 &&
                    renderRowGroup(t('hospitalization.detail.phRec.changelog.delete'), 'deleted-rows', change.deleteRows)}
                </>
              )}
            </div>
          ))}
          {parsedChangeLog.length === 0 && <Typography>{t('chart.emptyData')}</Typography>}
        </Modal.Body>
        <Modal.Footer css={styles.modalFooter} />
      </div>
    </Modal>
  );
};

const getStyles = () => ({
  modalContent: css`
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 60px);
  `,
  modalBody: css`
    flex: 1 1 auto;
    overflow-y: auto;
  `,
  modalFooter: css`
    height: 8px;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--dlThemeGray-900);
  `,
  changelogItem: (isOpen: boolean) => css`
    background: ${isOpen ? 'var(--dlThemeGray-100)' : 'var(--dlThemeGray-50)'};
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;
    transition: background-color 0.2s ease-in-out;

    &:hover {
      background: var(--dlThemeGray-100);
    }
  `,
  changelogItemHeader: css`
    background: transparent;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:hover {
      background: var(--dlThemeGray-100);
    }
  `,
  changelogItemHeaderLeft: css`
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--dlTheme-700);
    overflow: hidden;
  `,
  changelogItemHeaderText: css``,
  changelogItemHeaderTextShrink: css`
    flex-shrink: 1;
  `,
  changelogItemHeaderSpacer: css`
    width: 1px;
    height: 16px;
    background: var(--dlTheme-700);
  `,
  changelogItemHeaderIconButton: css`
    padding: 0;
    width: 16px;
    height: 16px;
  `,
  changelogItemHeaderIcon: css`
    width: 16px;
    height: 16px;
    cursor: pointer;
  `,
  rowGroup: css`
    padding: 0 12px 8px 12px;
  `,
  rowGroupLabel: css`
    font-weight: bold;
    margin: 8px 0 8px 0;
    color: var(--dlThemeGray-500);
  `,
  row: css`
    display: flex;
    padding: 6px 8px;
    background: var(--dlThemeGray-50);
    border-radius: 4px;
    margin-bottom: 4px;
    justify-content: space-between;

    &:hover {
      background: var(--dlThemeGray-50);
    }
  `,
  key: css`
    font-weight: 600;
    color: var(--dlThemeGray-700);
  `,
  value: css`
    color: var(--dlThemeGray-800);
    text-align: right;
  `,
});
